/**
 * Reads answered from the device's own store.
 *
 * The working set is already on the phone; without this it may as well not be.
 * These functions assemble the same shapes the routes already consume, so a
 * screen switches from network to store by changing which function it calls.
 *
 * Two things are deliberately *not* here:
 *
 *  * **The office's screens.** Dispatch, quotes and invoices are desk work,
 *    and the device only carries this tech's own jobs on purpose (D16). A
 *    local "dispatch board" built from one technician's working set would be
 *    a convincing, wrong picture of the day.
 *  * **Anything the device cannot compute honestly.** Quote totals need kit
 *    expansion and margin arithmetic that is still Python-only (MOBILE-4), so
 *    quotes are not read from here. Job costing *is*, because `costing.ts` is
 *    a tested port — see `parity.test.ts`.
 */

import { costJob, type CostLine, type CostTime, type JobCosting } from "./costing";
import * as servicecall from "./servicecall";
import { expandKit, priceQuote, type ItemSpec, type KitLineSpec, type KitSpec } from "./pricing";
import * as outbox from "./outbox";
import { api } from "./api";
import { all, get, put, type Row, where } from "./store";
import type { JobDetailOut, JobOut, JobStatus, JobType } from "./types";
import { standing, type Standing, type VariationSummary } from "./variations";

/* ------------------------------------------------------------------ jobs */

function num(value: unknown, fallback = 0): number {
  const n = typeof value === "string" ? Number(value) : (value as number);
  return Number.isFinite(n) ? n : fallback;
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** A tech's own jobs, soonest first. The run sheet. */
export async function myJobs(userId: string): Promise<JobOut[]> {
  const rows = await where<Row>("job", "assigned_user_id", userId);
  return rows.map(toJobOut).sort(bySchedule);
}

/**
 * Every job on the device, including the unassigned callout queue.
 *
 * The working set carries those on purpose so a tech can be diverted
 * mid-morning without a signal.
 */
export async function jobs(): Promise<JobOut[]> {
  return (await all<Row>("job")).map(toJobOut).sort(bySchedule);
}

function bySchedule(a: JobOut, b: JobOut): number {
  // Unscheduled last: a job with no time on it is not "midnight", it is
  // waiting for someone to book it.
  if (!a.scheduled_start && !b.scheduled_start) return a.number.localeCompare(b.number);
  if (!a.scheduled_start) return 1;
  if (!b.scheduled_start) return -1;
  return a.scheduled_start.localeCompare(b.scheduled_start);
}

function toJobOut(row: Row): JobOut {
  return {
    id: str(row.id),
    // A job created offline has no server number yet; the provisional one is
    // what the customer was shown, so it is what to display (D10).
    number: str(row.number) || str(row.client_ref) || "—",
    customer_id: str(row.customer_id),
    site_id: str(row.site_id),
    // Cast rather than validate: these come off the server through the
    // working set, so an unknown value means the server shipped ahead of
    // this build — and showing it verbatim beats refusing to render the job.
    type: (str(row.type) || "service") as JobType,
    status: (str(row.status) || "unscheduled") as JobStatus,
    title: str(row.title),
    description: (row.description as string | null) ?? null,
    work_done: (row.work_done as string | null) ?? null,
    purchase_order_number: (row.purchase_order_number as string | null) ?? null,
    assigned_user_id: (row.assigned_user_id as string | null) ?? null,
    scheduled_start: (row.scheduled_start as string | null) ?? null,
    scheduled_end: (row.scheduled_end as string | null) ?? null,
    sla_due_at: (row.sla_due_at as string | null) ?? null,
    arrived_at: (row.arrived_at as string | null) ?? null,
    completed_at: (row.completed_at as string | null) ?? null,
    billing_mode: str(row.billing_mode) || "time_and_materials",
    adjustment_bp: (row.adjustment_bp as number | null) ?? null,
    adjustment_cents: (row.adjustment_cents as number | null) ?? null,
    adjustment_label: (row.adjustment_label as string | null) ?? null,
    needs_return: row.needs_return === true,
    return_reason: (row.return_reason as string | null) ?? null,
    return_note: (row.return_note as string | null) ?? null,
    return_hours: row.return_hours === null || row.return_hours === undefined ? null : num(row.return_hours),
    left_site_at: (row.left_site_at as string | null) ?? null,
    travel_km_one_way: row.travel_km_one_way === null || row.travel_km_one_way === undefined ? null : num(row.travel_km_one_way),
    quoted_cents: num(row.quoted_cents),
    quoted_hours: row.quoted_hours == null ? null : num(row.quoted_hours),
    overrun_accepted_hours: row.overrun_accepted_hours == null ? null : num(row.overrun_accepted_hours),
    revenue_cents: num(row.revenue_cents),
    cost_cents: num(row.cost_cents),
    margin_bp: num(row.margin_bp),
    updated_at: str(row.updated_at),
  };
}

/**
 * One job with its lines and a costing computed here, not remembered.
 *
 * Recomputed rather than read off the row on purpose. The denormalised
 * `margin_bp` on the job is whatever the server last worked out, and a tech
 * who has just added three parts offline would be shown a margin that predates
 * them — a stale number presented as a live one, which is worse than no number
 * because it invites a decision.
 */
export async function job(id: string): Promise<JobDetailOut | null> {
  const row = await get<Row>("job", id);
  if (!row) return null;

  const base = toJobOut(row);
  const [lineRows, timeRows, variationRows] = await Promise.all([
    where<Row>("job_line", "job_id", id),
    where<Row>("time_entry", "job_id", id),
    where<Row>("variation", "job_id", id),
  ]);

  // In the order the office and the tech see on the server. IndexedDB hands
  // rows back by primary key, which is a uuid, which is to say at random —
  // so a part added on site appeared somewhere in the middle of the list
  // (D73). `sort` ties are broken by id so the order is at least stable.
  lineRows.sort((a, b) => num(a.sort) - num(b.sort) || str(a.id).localeCompare(str(b.id)));

  const lines: CostLine[] = lineRows.map((line) => ({
    description: str(line.description),
    qty: num(line.qty, 1),
    unit_cost_cents: num(line.unit_cost_cents),
    unit_sell_cents: num(line.unit_sell_cents),
    kind: str(line.kind) || "part",
    billable: line.billable !== false,
    under_warranty: line.under_warranty === true,
  }));

  // A break is a pause, not hours or cost on the job (F7, D86).
  const times: CostTime[] = timeRows.filter((entry) => str(entry.kind) !== "break").map((entry) => ({
    minutes: num(entry.minutes),
    cost_cents_per_hour: num(entry.cost_cents),
    charge_cents_per_hour: num(entry.charge_cents),
    billable: entry.billable !== false,
    kind: str(entry.kind) || "on_site",
  }));

  const where_ = jobStanding(base, variationRows, times);
  // A service call's fee, blocks and km, from the same rows and the same
  // arithmetic the server uses (D86). Absent on any other kind of job.
  const [orgRows, customerRow, siteRow] = await Promise.all([
    all<Row>("organisation"),
    get<Row>("customer", str(row.customer_id)),
    get<Row>("site", str(row.site_id)),
  ]);
  const clocked = timeRows.map((e) => ({ kind: str(e.kind) || "on_site", billable: e.billable !== false, minutes: num(e.minutes) }));
  const service = servicecall.forJob(row, clocked, orgRows[0] ?? null, customerRow ?? null, siteRow ?? null);
  const costing = costJob(lines, times, {
    service_revenue_cents: service === null ? null : servicecall.totalCents(service),
    adjustment_bp: row.adjustment_bp === null || row.adjustment_bp === undefined ? null : num(row.adjustment_bp),
    adjustment_cents:
      row.adjustment_cents === null || row.adjustment_cents === undefined ? null : num(row.adjustment_cents),
    quoted_cents: base.quoted_cents || null,
    quoted_hours: where_.hours_ceiling,
    approved_variation_cents: where_.approved_cents,
    has_unapproved_variation: where_.pending_count > 0,
  });

  return {
    ...base,
    lines: lineRows.map((line) => ({
      id: str(line.id),
      catalogue_item_id: (line.catalogue_item_id as string | null) ?? null,
      asset_id: (line.asset_id as string | null) ?? null,
      kind: str(line.kind) || "part",
      description: str(line.description),
      qty: num(line.qty, 1),
      unit_cost_cents: num(line.unit_cost_cents),
      unit_sell_cents: num(line.unit_sell_cents),
      billable: line.billable !== false,
      under_warranty: line.under_warranty === true,
      serial: (line.serial as string | null) ?? null,
    })),
    // The rows themselves, not just their cost: the Time card lists them.
    // Without this the card said "No time booked yet" on a job with 31 hours
    // on it, while the margin card beside it counted every one (D84).
    time_entries: timeRows.map((entry) => ({
      id: str(entry.id),
      user_id: (entry.user_id as string | null) ?? null,
      kind: str(entry.kind) || "on_site",
      started_at: (entry.started_at as string | null) ?? null,
      ended_at: (entry.ended_at as string | null) ?? null,
      minutes: num(entry.minutes),
      billable: entry.billable !== false,
      cost_cents: num(entry.cost_cents),
      charge_cents: num(entry.charge_cents),
    })),
    costing: toCostingOut(costing, where_),
    // What the server last said about how it bills (D86), kept on the row by
    // `absorb`; the phone recomputes the lines from its `rates` as time runs.
    service: (row.service as JobDetailOut["service"]) ?? null,
  };
}

/** Where a job stands on variations and hours, from what the device holds. */
export function jobStanding(base: JobOut, variationRows: Row[], times: CostTime[], proposedHours = 0): Standing {
  const summaries: VariationSummary[] = variationRows.map((v) => ({
    status: str(v.status) || "draft",
    amount_cents: num(v.amount_cents),
    hours: num(v.hours),
  }));
  const hoursActual = times.reduce((sum, t) => sum + t.minutes, 0) / 60;

  return standing(summaries, {
    quoted_hours: base.quoted_hours,
    hours_actual: hoursActual,
    overrun_accepted_hours: base.overrun_accepted_hours,
    proposed_hours: proposedHours,
  });
}

function toCostingOut(costing: JobCosting, where_: Standing) {
  return {
    revenue_cents: costing.revenue_cents,
    cost_cents: costing.cost_cents,
    margin_cents: costing.margin_cents,
    margin_bp: costing.margin_bp,
    hours_actual: costing.hours_actual,
    hours_quoted: costing.hours_quoted,
    headroom_cents: costing.headroom_cents,
    guard: costing.guard,
    // The variation warnings name the money and the count; the generic
    // costing ones do not. Same precedence as the server's `_detail`.
    warnings: where_.warnings.length > 0 ? where_.warnings : costing.warnings,
    hours_ceiling: where_.hours_ceiling,
    pending_variations: where_.pending_count,
    approved_variation_cents: where_.approved_cents,
  };
}

/** Variations on a job, oldest first — the order they were raised. */
export async function variations(jobId: string): Promise<Row[]> {
  const rows = await where<Row>("variation", "job_id", jobId);
  return rows.sort((a, b) => str(a.created_at).localeCompare(str(b.created_at)));
}

/* ------------------------------------------------------------- customers */

/** Sites the device is carrying, with their customer's name for the picker. */
export async function sitePicker(): Promise<{ id: string; label: string; customer_id: string }[]> {
  const [sites, customers] = await Promise.all([all<Row>("site"), all<Row>("customer")]);
  const byId = new Map(customers.map((c) => [str(c.id), str(c.name)]));

  return sites
    .map((site) => ({
      id: str(site.id),
      customer_id: str(site.customer_id),
      label: `${byId.get(str(site.customer_id)) ?? "Unknown customer"} — ${str(site.name)}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/* ----------------------------------------------------------------- sites */

/** A site with its asset register — what a tech opens on arrival. */
export async function site(id: string) {
  const row = await get<Row>("site", id);
  if (!row) return null;

  const [assets, customer, openJobs] = await Promise.all([
    where<Row>("asset", "site_id", id),
    get<Row>("customer", str(row.customer_id)),
    where<Row>("job", "site_id", id),
  ]);

  return {
    id: str(row.id),
    customer_id: str(row.customer_id),
    customer_name: customer ? str(customer.name) : "",
    name: str(row.name),
    address_line: (row.address_line as string | null) ?? null,
    suburb: (row.suburb as string | null) ?? null,
    state: (row.state as string | null) ?? null,
    postcode: (row.postcode as string | null) ?? null,
    access_notes: (row.access_notes as string | null) ?? null,
    induction_required: row.induction_required === true,
    swms_required: row.swms_required === true,
    asset_count: assets.length,
    open_job_count: openJobs.filter((j) => !["completed", "invoiced", "cancelled"].includes(str(j.status))).length,
    contract_code: null,
  };
}

export async function assets(siteId: string): Promise<Row[]> {
  const rows = await where<Row>("asset", "site_id", siteId);
  return rows.sort((a, b) => str(a.ref).localeCompare(str(b.ref), undefined, { numeric: true }));
}

/**
 * What the last tech found, newest first.
 *
 * The most useful thing on a site and the thing a job-only sync leaves behind
 * (D16), which is why the working set carries it.
 */
export async function assetHistory(assetId: string): Promise<Row[]> {
  const rows = await where<Row>("asset_event", "asset_id", assetId);
  return rows.sort((a, b) => str(b.occurred_at).localeCompare(str(a.occurred_at)));
}

export type InstallState =
  /** Queued on this phone, not sent yet. */
  | { state: "pending"; serial: string }
  /** The server has it; its own values come back on the pull that follows. */
  | { state: "sent"; serial: string }
  /** The server refused it — usually a serial already on another asset. */
  | { state: "refused"; serial: string; reason: string };

/**
 * Where each offline scan has got to, by asset, from the outbox.
 *
 * Read from the queue rather than a flag on the asset because the queue is
 * the only thing that knows about a refusal. A scan the server turned down
 * must put the asset back on the worklist with the reason, not leave it
 * showing a serial the register does not have (D15, D47).
 */
export async function installStates(): Promise<Map<string, InstallState>> {
  const latest = new Map<string, outbox.Queued>();
  for (const change of await outbox.all()) {
    if (change.entity !== "asset_event" || change.op !== "create") continue;
    const value = (change.value ?? {}) as Record<string, unknown>;
    if (value.kind !== "installed") continue;
    const assetId = str(value.asset_id);
    const prior = latest.get(assetId);
    if (!prior || (change.seq ?? 0) >= (prior.seq ?? 0)) latest.set(assetId, change);
  }

  const out = new Map<string, InstallState>();
  for (const [assetId, change] of latest) {
    const value = (change.value ?? {}) as Record<string, unknown>;
    const serial = str((value.payload as Record<string, unknown> | undefined)?.serial);
    if (change.rejected_at) out.set(assetId, { state: "refused", serial, reason: change.reason ?? "refused" });
    else if (change.sent_at) out.set(assetId, { state: "sent", serial });
    else out.set(assetId, { state: "pending", serial });
  }
  return out;
}

/**
 * The hardware this job is putting in: what is still to scan, and what is in.
 *
 * Read from the device, so the list opens in a plant room — which is where
 * the scanning happens. An asset scanned offline carries its serial locally
 * straight away (`actions.commission` patches it), so it moves to "in" on the
 * screen before the server has heard about it, marked as waiting to send —
 * and moves back, with the reason, if the server refuses it.
 */
export async function installRegister(
  jobId: string,
): Promise<{ awaiting: (Row & { refused?: string })[]; installed: (Row & { install?: InstallState })[] }> {
  const [rows, states] = await Promise.all([
    where<Row>("asset", "installed_by_job_id", jobId),
    installStates(),
  ]);
  const live = rows.filter((r) => !r.deleted_at);
  live.sort((a, b) => str(a.ref).localeCompare(str(b.ref), undefined, { numeric: true }));

  const awaiting: (Row & { refused?: string })[] = [];
  const installed: (Row & { install?: InstallState })[] = [];
  for (const row of live) {
    const install = states.get(str(row.id));
    if (install?.state === "refused") {
      awaiting.push({ ...row, refused: `${install.serial}: ${install.reason}` });
    } else if (row.serial) {
      // No queue entry but still flagged: it was sent and the entry has since
      // been flushed, and the pull that follows has not landed yet. Since
      // SYNC-6 that pull carries the server's own row, so the flag clears
      // itself rather than sitting until the next working set.
      installed.push({
        ...row,
        install: install ?? (row.pending_install ? { state: "sent", serial: str(row.serial) } : undefined),
      });
    } else {
      awaiting.push(row);
    }
  }
  return { awaiting, installed };
}

/* ------------------------------------------------------------- reference */

/**
 * The organisation row, for the settings the device needs to apply a rule
 * locally — the field approval limit above all.
 *
 * Returns null when the working set has not landed yet, and callers treat
 * that as "assume nothing is approvable in the field" rather than guessing a
 * limit. A tech told their $4,000 variation is approved when it is not is
 * worse off than one told to ring the office.
 */
export async function org(): Promise<Row | null> {
  const rows = await all<Row>("organisation");
  return rows[0] ?? null;
}

export async function catalogue(): Promise<Row[]> {
  return (await all<Row>("catalogue_item")).sort((a, b) =>
    str(a.description).localeCompare(str(b.description)),
  );
}

export async function kits(): Promise<Row[]> {
  return (await all<Row>("kit")).sort((a, b) => str(a.name).localeCompare(str(b.name)));
}

/**
 * Expand a kit from what this device is carrying — the site walk, with no
 * signal.
 *
 * This is the feature the kit previewer exists for: spin the camera count in
 * a car park and watch the cable, the labour and the EWP days move with it.
 * It works here because `pricing.ts` is a tested port of the server's
 * expansion and margin arithmetic, held to the same fixtures (D23) — without
 * that, this would be a second opinion about a number the customer is about
 * to be quoted, which is worse than no opinion.
 *
 * Returns null when the kit is not on the device, so the caller can say that
 * rather than showing an empty preview as though the kit were empty.
 */
export async function kitPreview(
  kitId: string,
  driverQty: number,
  options: {
    targetMarginBp?: number | null;
    includeOptional?: Set<string>;
    taxRateBp?: number;
    /** "price_book" (new quotes, D87): priced lines keep their price-book sell. */
    pricing?: "price_book" | "target_margin";
  } = {},
): Promise<KitPreview | null> {
  const kitRow = await get<Row>("kit", kitId);
  if (!kitRow) return null;

  const [lineRows, items, orgRow] = await Promise.all([
    where<Row>("kit_line", "kit_id", kitId),
    all<Row>("catalogue_item"),
    org(),
  ]);

  const byId = new Map(items.map((item) => [str(item.id), item]));

  const lines: KitLineSpec[] = [];
  for (const line of lineRows) {
    const item = byId.get(str(line.catalogue_item_id));
    // A kit line whose item is not on the device is skipped rather than
    // priced at zero — a silent zero would quietly understate the quote.
    if (!item) continue;

    lines.push({
      item: toItemSpec(item),
      qty_formula: str(line.qty_formula) || "n",
      include_by_default: line.include_by_default !== false,
      is_optional: line.is_optional === true,
      sort: num(line.sort),
      note: (line.note as string | null) ?? null,
    });
  }

  const kit: KitSpec = {
    code: str(kitRow.code),
    name: str(kitRow.name),
    driver_label: str(kitRow.driver_label) || "quantity",
    lines,
  };

  const section = expandKit(kit, driverQty, {
    includeOptional: options.includeOptional,
    defaultMarginBp: options.targetMarginBp ?? null,
  });
  // A price-book quote totals its lines as priced; only the old kind re-derives
  // the whole thing from cost at the target margin (D87). The target margin
  // still prices the lines that have no price of their own, above.
  const totals = priceQuote([section], {
    targetMarginBp: (options.pricing ?? "price_book") === "target_margin" ? (options.targetMarginBp ?? null) : null,
    taxRateBp: options.taxRateBp ?? (orgRow ? num(orgRow.tax_rate_bp, 1000) : 1000),
  });

  return {
    kit: { id: str(kitRow.id), code: kit.code, name: kit.name, driver_label: kit.driver_label ?? "quantity" },
    driver_qty: driverQty,
    lines: section.lines.map((line) => ({
      // Not shown on the preview, but `queueQuote` builds the stored record
      // from these lines, and acceptance keys on both (D48).
      item_id: line.item_id,
      creates_asset: line.creates_asset,
      description: line.description,
      kind: line.kind,
      qty: line.qty,
      uom: line.uom,
      unit_cost_cents: line.unit_cost_cents,
      unit_sell_cents: line.unit_sell_cents,
      cost_cents: line.cost_cents,
      sell_cents: line.sell_cents,
    })),
    totals: {
      cost_cents: totals.cost_cents,
      subtotal_cents: totals.subtotal_cents,
      tax_cents: totals.tax_cents,
      total_cents: totals.total_cents,
      margin_bp: totals.margin_bp,
    },
    /** The lines dropped because their catalogue item has not synced. */
    missing_items: lineRows.length - lines.length,
  };
}

export type KitPreview = {
  kit: { id: string; code: string; name: string; driver_label: string };
  driver_qty: number;
  lines: {
    item_id: string;
    creates_asset: boolean;
    description: string;
    kind: string;
    qty: number;
    uom: string;
    unit_cost_cents: number;
    unit_sell_cents: number;
    cost_cents: number;
    sell_cents: number;
  }[];
  totals: { cost_cents: number; subtotal_cents: number; tax_cents: number; total_cents: number; margin_bp: number };
  missing_items: number;
};

function toItemSpec(item: Row): ItemSpec {
  return {
    id: str(item.id),
    description: str(item.description),
    kind: str(item.kind) || "part",
    uom: str(item.uom) || "ea",
    cost_cents: num(item.cost_cents),
    sell_cents: num(item.sell_cents),
    margin_bp: item.margin_bp == null ? null : num(item.margin_bp),
    creates_asset: item.asset_category_id != null,
  };
}

/** The van's stock, so a tech knows what is behind them before promising it. */
export interface Fittable {
  item: Row;
  /** Whether this is the kind of thing a van carries. */
  stocked: boolean;
  /** How many this device believes are on the tech's own van. */
  on_van: number;
  /** The van itself, so the line can say where the part came from. */
  van_id: string | null;
}

/**
 * The price book, as a tech picking a part sees it (JOB-9).
 *
 * Read from the device, not the API, because fitting a part is the thing that
 * happens in a comms room with no signal. The van count comes with it: a tech
 * choosing between two cameras wants to know which one is in the back of the
 * vehicle, and that question has no answer on a screen that only lists a
 * catalogue.
 *
 * Nothing is hidden for being out of stock. A part that is physically in the
 * tech's hand gets recorded whatever the count says — the count is what is
 * wrong in that case, and refusing the line would teach people to stop
 * recording parts (D20).
 */
export async function fittable(search: string, userId: string): Promise<Fittable[]> {
  const [items, levels, locations] = await Promise.all([
    all<Row>("catalogue_item"),
    all<Row>("stock_level"),
    all<Row>("stock_location"),
  ]);

  const van = locations.find(
    (row) => str(row.assigned_user_id) === userId && str(row.kind) === "van",
  );
  const onVan = new Map<string, number>();
  if (van) {
    for (const level of levels) {
      if (str(level.location_id) === str(van.id)) onVan.set(str(level.catalogue_item_id), num(level.qty));
    }
  }

  const needle = search.trim().toLowerCase();
  return items
    .filter((item) => item.active !== false)
    .filter((item) => {
      if (!needle) return true;
      return (
        str(item.description).toLowerCase().includes(needle) || str(item.sku).toLowerCase().includes(needle)
      );
    })
    .map((item) => ({
      item,
      // Labour, fees and hire are not on anybody's van, and showing "— on
      // van" against an hour of commissioning made the column meaningless.
      stocked: str(item.kind) === "part",
      on_van: onVan.get(str(item.id)) ?? 0,
      van_id: van ? str(van.id) : null,
    }))
    // What is on the van first: the part in the vehicle is the one being
    // fitted far more often than the one that has to be ordered.
    .sort((a, b) => b.on_van - a.on_van || str(a.item.description).localeCompare(str(b.item.description)))
    .slice(0, 40);
}

export interface Place {
  id: string;
  name: string;
  kind: string;
  /** How many of the item in question this place is holding. */
  qty: number;
}

/**
 * Where a part could be coming off, for the item being added (JOB-9/D75).
 *
 * The tech's own van first, then anywhere else that holds one, then the rest.
 * An office user adding a line on somebody's behalf has no van of their own,
 * and before this the line recorded no movement at all and said nothing —
 * so the part left the system's idea of the world without leaving anywhere.
 */
export async function placesFor(itemId: string, userId: string): Promise<Place[]> {
  let [levels, locations] = await Promise.all([all<Row>("stock_level"), all<Row>("stock_location")]);

  // A technician's working set carries their own van and nothing else (D16),
  // which is right for the phone and wrong for this list: the office adding a
  // line on somebody's behalf needs the store and the other vans, and was
  // being offered only their own — which for an office user is nothing at
  // all. Ask the server when it can be reached, and fall back to the device.
  let served: Row[] | null = null;
  try {
    const [places, held] = await Promise.all([
      api.stock.locations(),
      api.stock.levels({ include_empty: true }),
    ]);
    served = places.map((row) => ({ ...row }) as unknown as Row);
    // Levels from the same place as the locations, or the store shows up
    // holding nothing because this device was never carrying its levels.
    levels = held
      .filter((row) => row.catalogue_item_id === itemId)
      .map((row) => ({
        id: `${row.location_id}:${row.catalogue_item_id}`,
        location_id: row.location_id,
        catalogue_item_id: row.catalogue_item_id,
        qty: row.qty,
      }));
  } catch {
    // Offline. The van in the tech's hand is the one that matters anyway.
  }
  if (served !== null) locations = served;
  const held = new Map<string, number>();
  for (const level of levels) {
    if (str(level.catalogue_item_id) === itemId) held.set(str(level.location_id), num(level.qty));
  }
  const mine = (row: Row) => str(row.assigned_user_id) === userId && str(row.kind) === "van";
  return locations
    .map((row) => ({
      id: str(row.id),
      name: str(row.name),
      kind: str(row.kind),
      qty: held.get(str(row.id)) ?? 0,
      own: mine(row) ? 1 : 0,
    }))
    .sort((a, b) => b.own - a.own || b.qty - a.qty || a.name.localeCompare(b.name))
    .map(({ own: _own, ...place }) => place);
}

export async function vanStock(): Promise<{ item: Row; level: Row }[]> {
  const [levels, items] = await Promise.all([all<Row>("stock_level"), all<Row>("catalogue_item")]);
  const byId = new Map(items.map((item) => [str(item.id), item]));

  return levels
    .map((level) => ({ level, item: byId.get(str(level.catalogue_item_id)) }))
    .filter((pair): pair is { item: Row; level: Row } => pair.item !== undefined)
    .sort((a, b) => str(a.item.description).localeCompare(str(b.item.description)));
}

/**
 * A job this device doesn't carry, fetched from the server and kept.
 *
 * The working set is a technician's own run (D16), so the office opening a
 * job it just created — or anyone opening someone else's — finds nothing
 * locally. Rather than "not on this device" while online, fetch it and put
 * it in the store, so the screen and its actions work as for any other job.
 * Returns null when offline or when the server has no such job either.
 */
export async function fetchJob(id: string): Promise<JobDetailOut | null> {
  let detail: JobDetailOut;
  try {
    detail = await api.jobs.get(id);
  } catch {
    return null;
  }
  const {
    lines,
    time_entries: times,
    costing: _costing,
    stock: _stock,
    ...fields
  } = detail as JobDetailOut & { stock?: unknown };
  await put("job", { ...(fields as unknown as Row), id: detail.id });
  if (lines.length) {
    await put(
      "job_line",
      lines.map((line) => ({ ...(line as unknown as Row), id: line.id, job_id: detail.id })),
    );
  }
  // The hours too, or the margin on this screen is too good by exactly the
  // labour on the job: `job()` recomputes from what the device holds, and a
  // job fetched rather than synced used to arrive with its lines and none of
  // its time (D73).
  if (times?.length) {
    await put(
      "time_entry",
      times.map((entry) => ({ ...(entry as unknown as Row), id: entry.id, job_id: detail.id })),
    );
  }
  return job(id);
}

/* ------------------------------------------------------- tests (COMPLY-1) */

export type TestableAsset = {
  asset: Row;
  template: Row | null;
  /** The last sheet this phone knows about, newest first. */
  last: Row | null;
};

/**
 * What can be tested on this job, and which sheet each one uses.
 *
 * Read from the device rather than the network, because the plant room a
 * panel lives in is the worst reception in the building. The template is
 * matched by the asset's category, falling back to a sheet that applies to
 * everything — the same rule the server uses on `/forms/due`, so the tech
 * sees the sheet they will actually be judged against.
 */
export async function testable(jobId: string): Promise<TestableAsset[]> {
  const detail = await get<Row>("job", jobId);
  if (!detail) return [];
  const onSite = await where<Row>("asset", "site_id", str(detail.site_id));
  if (onSite.length === 0) return [];

  const templates = (await all<Row>("form_template")).filter(
    (t) => !t.deleted_at,
  );
  const byCategory = new Map<string, Row>();
  let any: Row | null = null;
  for (const template of templates) {
    const category = template.applies_to_category_id;
    if (typeof category === "string") {
      if (!byCategory.has(category)) byCategory.set(category, template);
    } else if (!any) {
      any = template;
    }
  }

  const submissions = (await all<Row>("form_submission")).filter(
    (s) => !s.deleted_at,
  );
  const latest = new Map<string, Row>();
  for (const submission of submissions) {
    const asset = str(submission.asset_id);
    if (!asset) continue;
    const held = latest.get(asset);
    if (!held || str(submission.submitted_at) > str(held.submitted_at))
      latest.set(asset, submission);
  }

  return onSite
    .filter((asset) => asset.status !== "retired")
    .sort((a, b) => str(a.ref).localeCompare(str(b.ref), undefined, { numeric: true }))
    .map((asset) => ({
      asset,
      template: byCategory.get(str(asset.category_id)) ?? any,
      last: latest.get(str(asset.id)) ?? null,
    }))
    .filter((row) => row.template !== null);
}

/**
 * What is this thing, from the device's own copy (ASSET-4).
 *
 * The question a tech asks standing in front of unfamiliar hardware. The
 * server can answer it for anything in the business; this answers it for the
 * sites the tech is actually visiting, which is where they are standing, and
 * it answers underground.
 *
 * Case-folded, because a label prints what the manufacturer chose and nobody
 * types it back the same way — the same rule the server's `find_clash` uses.
 */
export async function bySerial(serial: string): Promise<{ asset: Row; site: Row | null } | null> {
  const wanted = serial.trim().toLowerCase();
  if (!wanted) return null;
  const match = (await all<Row>("asset")).find(
    (a) => !a.deleted_at && typeof a.serial === "string" && a.serial.toLowerCase() === wanted,
  );
  if (!match) return null;
  return { asset: match, site: (await get<Row>("site", str(match.site_id))) ?? null };
}
